<script setup lang="ts">
import { getToken } from '@josempgon/vue-keycloak'
import type { DataTableHeaders } from '@/plugins/vuetify';
import type MDBAlerte from '@/types/alerte';
import { notifyError, toDate_wd_dd_MMMM_hh_mm } from '@/utils/helpers'



const theme = useTheme()
const storeU = useUtilisateurStore()
const isLoading = ref(false)

const jwtCopied = ref(false)
const jwtToken = ref<string | null>(null)
async function loadAndCopyJwtToken() {
  const token = await getToken()
  if (!token) return
  jwtToken.value = token
  try {
    await navigator.clipboard.writeText(token)
  } catch {
    // Fallback for non-secure contexts
    const el = document.createElement('textarea')
    el.value = token
    el.style.cssText = 'position:fixed;opacity:0;pointer-events:none'
    document.body.appendChild(el)
    el.focus()
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
  }
  jwtCopied.value = true
  setTimeout(() => { jwtCopied.value = false }, 2000)
}

// ── Administration alerts widget ───────────────────────────
interface SévéritéInfo {
  label: string
  color: string
  icon: string
}

function getSévérité(sévérité: number): SévéritéInfo {
  switch (sévérité) {
    case 1: return { label: 'Info', color: 'info', icon: 'M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z' }
    case 2: return { label: 'Avertissement', color: 'warning', icon: 'M12,2L1,21H23M12,6L19.53,19H4.47M11,10V14H13V10M11,16V18H13V16' }
    case 3: return { label: 'Erreur', color: 'error', icon: 'M11,15H13V17H11V15M11,7H13V13H11V7M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20Z' }
    case 4: return { label: 'Critique', color: 'error', icon: 'M8.27,3L3,8.27V15.73L8.27,21H15.73C17.5,19.24 21,15.73 21,15.73V8.27L15.73,3M9.1,5H14.9L19,9.1V14.9L14.9,19H9.1L5,14.9V9.1M11,15H13V17H11V15M11,7H13V13H11V7' }
    default: return { label: 'Debug', color: 'grey', icon: 'M20,8H17.19C16.74,7.2 16.12,6.5 15.37,6L17,4.41L15.59,3L13.42,5.17C12.96,5.06 12.5,5 12,5C11.5,5 11.05,5.06 10.59,5.17L8.41,3L7,4.41L8.62,6C7.87,6.5 7.26,7.21 6.81,8H4V10H6.09C6.03,10.33 6,10.66 6,11V12H4V14H6V15C6,15.34 6.03,15.67 6.09,16H4V18H6.81C8.47,20.87 12.14,21.84 15,20.18C15.91,19.66 16.67,18.9 17.19,18H20V16H17.91C17.97,15.67 18,15.34 18,15V14H20V12H18V11C18,10.66 17.97,10.33 17.91,10H20V8M16,15A4,4 0 0,1 12,19A4,4 0 0,1 8,15V11A4,4 0 0,1 12,7A4,4 0 0,1 16,11V15M14,10V12H10V10H14M10,14H14V16H10V14Z' }
  }
}

const alertes = ref([] as MDBAlerte[])
const isLoadingAlertes = ref(false)
const searchAlertes = ref('')

const MESSAGE_MAX_LENGTH = 180
const expandedMessages = ref(new Set<string>())

function isMessageLong(message: string): boolean {
  return (message?.length ?? 0) > MESSAGE_MAX_LENGTH
}

function getMessagePreview(message: string): string {
  return message.slice(0, MESSAGE_MAX_LENGTH).trimEnd() + '…'
}

function toggleMessage(id: string) {
  if (expandedMessages.value.has(id))
    expandedMessages.value.delete(id)
  else
    expandedMessages.value.add(id)
  expandedMessages.value = new Set(expandedMessages.value)
}

const alertesHeaders: DataTableHeaders = [
  { title: 'Date', key: 'formattedCréeLe', width: '160px', sortable: false },
  { title: 'Créé par', key: 'crééPar', width: '140px', sortable: false },
  { title: 'Sévérité', key: 'sévérité', width: '120px', sortable: false },
  { title: 'Type', key: 'typeAlerte', width: '180px', sortable: false },
  { title: 'Message', key: 'message', sortable: false },
  { title: 'Actions', key: 'actions', width: '120px', sortable: false },
]

const formattedAlertes = computed(() => {
  return alertes.value
    .slice()
    .sort((a, b) => new Date(b.crééLe).getTime() - new Date(a.crééLe).getTime())
    .map(alerte => ({
      ...alerte,
      formattedCréeLe: toDate_wd_dd_MMMM_hh_mm(alerte.crééLe),
      message: alerte.message || 'Aucun message',
    }))
})

const storeApp = useAppStore()

async function getAlertes() {
  try {
    isLoadingAlertes.value = true
    const results = await apiProphet.get('api/alertes/systeme')
    alertes.value = (results.data as MDBAlerte[])
      .filter(alerte => alerte.destinataireType === 'Agrimétrie')
    storeApp.adminAlertCount = alertes.value.length
  }
  catch (error) {
    notifyError(error)
  }
  finally {
    isLoadingAlertes.value = false
  }
}

async function confirmer(alerte: MDBAlerte) {
  try {
    const config = { params: { confirmer: true } }
    const result = await apiProphet.put(`api/alertes/${alerte.id}/confirmer`, null, config)
    if (result.status == 200) {
      Notify.success('Alerte confirmée avec succès')
      getAlertes()
    }
  } catch (error) {
    notifyError(error)
  }
}

async function effacer(alerte: MDBAlerte) {
  try {
    const result = await apiProphet.delete(`api/alertes/${alerte.id}`)
    if (result.status == 200) {
      Notify.success('Alerte effacée avec succès')
      getAlertes()
    }
  } catch (error) {
    notifyError(error)
  }
}

const isConfirmingAll = ref(false)
async function confirmerTous() {
  try {
    isConfirmingAll.value = true
    const config = { params: { confirmer: true } }
    await Promise.all(
      alertes.value.map(alerte => apiProphet.put(`api/alertes/${alerte.id}/confirmer`, null, config))
    )
    Notify.success('Toutes les alertes ont été confirmées')
    getAlertes()
  } catch (error) {
    notifyError(error)
  } finally {
    isConfirmingAll.value = false
  }
}

const dialogRevisionsWatch = ref(false)
const revisionChoice = ref('buckets_commandes')
const revisionsHeaders: DataTableHeaders = [
  { title: 'Date', key: 'date', width: '200px' },
  { title: 'Raison', key: 'raison'}
];
const revisions = ref([] as { date: string; raison: string }[])
function openRevisionsWatch() {
  dialogRevisionsWatch.value = true
}
async function loadRevisions() {
  try {
    isLoading.value = true

    let siteCS = storeU.siteSélectionné.lettresDappel

    if (revisionChoice.value === 'cron' || revisionChoice.value === 'cron_prisme') {
      siteCS = 'omni'
    }

    const result = await apiProphet.get(`sites/${siteCS}/revisions/${revisionChoice.value}/raisons`)
    revisions.value = result.data.map((r: string) => {
      const parts = r.split('|')
      return { date: parts[0], raison: parts[1] }
    })
    revisions.value.reverse()
  }
  catch (e) {
    Notify.error(e)
  }
  finally {
    isLoading.value = false
  }
}

onMounted(() => {
  getAlertes()
})
</script>

<template>
  <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading || isLoadingAlertes">

    <!-- Page header -->
    <div class="page-header px-5 pt-5 pb-3">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

        <!-- Left: title -->
        <div class="d-flex align-center" style="gap: 12px;">
          <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
            <v-icon icon="M12,12H19C18.47,16.11 15.72,19.78 12,20.92V12H5V6.3L12,3.19M12,1L3,5V11C3,16.55 6.84,21.73 12,23C17.16,21.73 21,16.55 21,11V5L12,1Z" size="26"/>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Administration</div>
            <div class="text-caption text-medium-emphasis">Outils et alertes système</div>
          </div>
        </div>

        <!-- Right: actions -->
        <div class="d-flex align-center flex-wrap" style="gap: 8px;">
          <v-btn
            color="primary" variant="tonal" rounded="pill"
            prepend-icon="M13.5,8H12V13L16.28,15.54L17,14.33L13.5,12.25V8M13,3A9,9 0 0,0 4,12H1L4.96,16.03L9,12H6A7,7 0 0,1 13,5A7,7 0 0,1 20,12A7,7 0 0,1 13,19C11.07,19 9.32,18.21 8.06,16.94L6.64,18.36C8.27,20 10.5,21 13,21A9,9 0 0,0 22,12A9,9 0 0,0 13,3"
            @click="openRevisionsWatch">
            Revisions Watch
          </v-btn>
          <v-btn
            :color="jwtCopied ? 'success' : 'primary'"
            variant="tonal" rounded="pill"
            :prepend-icon="jwtCopied ? 'M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z' : 'M19,21H8V7H19M19,5H8A2,2 0 0,0 6,7V21A2,2 0 0,0 8,23H19A2,2 0 0,0 21,21V7A2,2 0 0,0 19,5M16,1H4A2,2 0 0,0 2,3V17H4V3H16V1Z'"
            @click="loadAndCopyJwtToken">
            {{ jwtCopied ? 'Copié !' : 'Copier JWT' }}
          </v-btn>
        </div>
      </div>
    </div>

    <v-divider/>

    <!-- Content -->
    <div class="pa-4">

      <!-- Administration alerts widget -->
      <v-card variant="outlined" rounded="lg" class="mb-4">
        <v-card-title class="d-flex align-center flex-wrap" style="gap: 10px;">
          <v-avatar color="error" variant="tonal" size="36" rounded="lg">
            <v-icon icon="M8.27,3L3,8.27V15.73L8.27,21H15.73C17.5,19.24 21,15.73 21,15.73V8.27L15.73,3M9.1,5H14.9L19,9.1V14.9L14.9,19H9.1L5,14.9V9.1M11,15H13V17H11V15M11,7H13V13H11V7" size="20"/>
          </v-avatar>
          <div style="flex: 1;">
            <div class="text-subtitle-1 font-weight-bold">Alertes Administration</div>
            <div class="text-caption text-medium-emphasis">Notifications destinées à l'administration</div>
          </div>
          <v-chip size="small" color="error" variant="tonal" label>
            {{ formattedAlertes.length }}
          </v-chip>
          <v-btn
            v-if="alertes.length > 0"
            color="success" variant="tonal" rounded="lg" size="small"
            prepend-icon="M0.41,13.41L6,19L7.41,17.58L1.83,12M22.24,5.58L11.66,16.17L7.5,12L6.07,13.41L11.66,19L23.66,7M18,7L16.59,5.58L10.24,11.93L11.66,13.34L18,7Z"
            :loading="isConfirmingAll"
            @click="confirmerTous">
            Tout confirmer
          </v-btn>
          <v-text-field
            v-model="searchAlertes"
            label="Rechercher"
            prepend-inner-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
            density="compact" variant="outlined" rounded="lg" hide-details clearable
            style="max-width: 260px; min-width: 160px;"/>
        </v-card-title>

        <v-divider/>

        <v-data-table
          :headers="alertesHeaders"
          :items="formattedAlertes"
          :search="searchAlertes"
          :loading="isLoadingAlertes"
          item-value="id"
          :hide-default-header="$vuetify.display.smAndDown"
          no-data-text="Aucune alerte d'administration">

          <template #item.sévérité="{ item }">
            <v-chip
              :color="getSévérité(item.sévérité).color"
              :prepend-icon="getSévérité(item.sévérité).icon"
              size="small" variant="tonal" label>
              {{ getSévérité(item.sévérité).label }}
            </v-chip>
          </template>

          <template #item.message="{ item }">
            <div class="message-cell py-2">
              <div class="message-text" style="white-space: pre-wrap; word-break: break-word;">{{ isMessageLong(item.message) && !expandedMessages.has(item.id) ? getMessagePreview(item.message) : item.message }}</div>
              <v-btn
                v-if="isMessageLong(item.message)"
                size="x-small" color="primary" variant="text" class="mt-1 px-1"
                :append-icon="expandedMessages.has(item.id) ? 'M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z' : 'M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z'"
                @click="toggleMessage(item.id)">
                {{ expandedMessages.has(item.id) ? 'Voir moins' : 'Voir plus' }}
              </v-btn>
            </div>
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex align-center" style="gap: 4px;">
              <v-tooltip text="Confirmer" location="top">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" color="success"
                    size="small" variant="text"
                    @click="confirmer(item)"/>
                </template>
              </v-tooltip>
              <v-tooltip text="Effacer" location="top">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19M8,9H16V19H8V9M15.5,4L14.5,3H9.5L8.5,4H5V6H19V4H15.5Z" color="error"
                    size="small" variant="text"
                    @click="effacer(item)"/>
                </template>
              </v-tooltip>
            </div>
          </template>

          <!-- Mobile card layout -->
          <template v-if="$vuetify.display.smAndDown" #item="{ item }">
            <tr>
              <td class="pa-0">
                <v-card variant="flat" class="alerte-mobile-card pa-3 ma-2" rounded="lg">
                  <div class="d-flex align-center justify-space-between mb-2" style="gap: 8px;">
                    <v-chip
                      :color="getSévérité(item.sévérité).color"
                      :prepend-icon="getSévérité(item.sévérité).icon"
                      size="small" variant="tonal" label>
                      {{ getSévérité(item.sévérité).label }}
                    </v-chip>
                  </div>

                  <div class="text-body-2 font-weight-bold">{{ item.typeAlerte }}</div>
                  <div class="text-caption text-medium-emphasis mb-2">{{ item.formattedCréeLe }}</div>

                  <div class="text-body-2 mb-1" style="white-space: pre-wrap; word-break: break-word;">{{ isMessageLong(item.message) && !expandedMessages.has(item.id) ? getMessagePreview(item.message) : item.message }}</div>
                  <v-btn
                    v-if="isMessageLong(item.message)"
                    size="x-small" color="primary" variant="text" class="mb-2 px-1"
                    :append-icon="expandedMessages.has(item.id) ? 'M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z' : 'M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z'"
                    @click="toggleMessage(item.id)">
                    {{ expandedMessages.has(item.id) ? 'Voir moins' : 'Voir plus' }}
                  </v-btn>

                  <div class="d-flex flex-column text-caption text-medium-emphasis mb-2" style="gap: 2px;">
                    <span><v-icon icon="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,6A2,2 0 0,0 10,8A2,2 0 0,0 12,10A2,2 0 0,0 14,8A2,2 0 0,0 12,6M12,13C14.67,13 20,14.33 20,17V20H4V17C4,14.33 9.33,13 12,13M12,14.9C9.03,14.9 5.9,16.36 5.9,17V18.1H18.1V17C18.1,16.36 14.97,14.9 12,14.9Z" size="14" class="me-1"/>Créé par : {{ item.crééPar }}</span>
                  </div>

                  <v-divider class="my-2"/>

                  <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                    <v-spacer/>
                    <v-btn
                      prepend-icon="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" color="success"
                      size="small" variant="text"
                      @click="confirmer(item)">
                      Confirmer
                    </v-btn>
                    <v-btn
                      icon="M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19M8,9H16V19H8V9M15.5,4L14.5,3H9.5L8.5,4H5V6H19V4H15.5Z" color="error"
                      size="small" variant="text"
                      @click="effacer(item)"/>
                  </div>
                </v-card>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>

      <!-- JWT token card -->
      <v-card v-if="jwtToken" variant="outlined" rounded="lg" class="pa-2">
        <v-card-subtitle class="mb-1">JWT Token</v-card-subtitle>
        <v-textarea
          :model-value="jwtToken"
          readonly
          auto-grow
          rows="3"
          variant="outlined"
          density="compact"
          hide-details
          style="font-family: monospace; font-size: 0.75rem;"
        />
      </v-card>

    </div>
  </v-card>

  <v-dialog v-model="dialogRevisionsWatch" max-width="90%" :fullscreen="$vuetify.display.smAndDown">
    <v-card style="z-index: -1">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h5 text-medium-emphasis ps-2">Revisions Watch</span>
        <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" @click="dialogRevisionsWatch = false"/>
      </v-card-title>

      <v-divider class="mb-4"/>

      <v-card-text>
        <v-container>
          <v-row>
            <v-col >
              <v-select
                v-model="revisionChoice"
                label="Tables à surveiller"
                :items="['cron', 'cron_prisme', 'alertes', 'champs', 'cultures', 'semis', 'buckets', 'commandes', 'buckets_commandes', 'degrejours', 'ogn_modeles', 'vegco_requetes']"
              />
            </v-col>
            <v-col cols="3">
              <v-btn @click="loadRevisions"><v-icon>M2 12C2 16.97 6.03 21 11 21C13.39 21 15.68 20.06 17.4 18.4L15.9 16.9C14.63 18.25 12.86 19 11 19C4.76 19 1.64 11.46 6.05 7.05C10.46 2.64 18 5.77 18 12H15L19 16H19.1L23 12H20C20 7.03 15.97 3 11 3C6.03 3 2 7.03 2 12Z</v-icon> Load Revisions</v-btn>
            </v-col>
          </v-row>
        </v-container>
        <v-data-table
          :headers="revisionsHeaders" :items="revisions" :loading="isLoading"
          :items-per-page="-1"/>
      </v-card-text>

      <v-divider class="mt-2"/>

      <v-card-actions class="my-2 d-flex justify-end mr-2">
        <v-btn color="primary" variant="flat" @click="dialogRevisionsWatch = false">OK</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Message cell ────────────────────────── */
.message-text {
  font-size: 0.8125rem;
  line-height: 1.4;
}

/* ── Mobile alert card ───────────────────── */
.alerte-mobile-card {
  border: 1px solid v-bind('theme.global.name.value === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"');
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.18)" : "rgba(var(--v-theme-primary), 0.02)"');
}
</style>
